import { useMemo, useState } from 'react';

import { Modal, Spinner, Text, Textarea, toastService } from '@thefarmersfront/kitchen';

import { FormField, ModalContentsContainer, ModalSection, Table } from '~/components/common';

import { useGetReturnDisposalIssueItemsQuery, usePutReturnDisposalClaimMutation } from '~/hooks/receive';
import { getReturnDisposalClaimItemsColumns } from '~/utils';

import { CLAIM_NOTICE, CLAIM_REASON_MAX_LENGTH, RETURN_DISPOSAL_REQUEST_MESSAGE, TOAST_MESSAGE } from '~/constants';

import ClaimImageUploader from './ClaimImageUploader';

import type { TypeReturnDisposalIssueItemApi } from '~/types/receive.types';

import styles from './ReceiveReturnDisposalClaimModal.module.scss';

type TypeReceiveReturnDisposalClaimModalProps = {
  isOpen: boolean;
  inspectionIds: number[];
  onClose: () => void;
  /** 신청 성공 시(부모에서 목록 재조회 + 모달 닫기) */
  onSuccess: () => void;
};

/**
 * [입고관리 > 회송/폐기 관리] 클레임 처리 신청 모달
 *
 * @note 안내 패널 + 상품정보 미리보기 + 클레임 정보(사유·거래명세서 이미지)를 입력해 신청한다.
 *       이미지는 신청 시점에 멀티파트로 함께 전송한다. 선택 게이팅(D+3)은 목록 버튼에서 처리한다.
 */
const ReceiveReturnDisposalClaimModal = ({
  isOpen,
  inspectionIds,
  onClose,
  onSuccess,
}: TypeReceiveReturnDisposalClaimModalProps) => {
  const issueItemsQuery = useGetReturnDisposalIssueItemsQuery({ inspectionIds, enabled: isOpen });
  const submitMutation = usePutReturnDisposalClaimMutation();

  const [reason, setReason] = useState('');
  const [images, setImages] = useState<File[]>([]);

  const issueItems = issueItemsQuery.data ?? [];

  const columns = useMemo(() => getReturnDisposalClaimItemsColumns(), []);

  const isValid = issueItems.length > 0 && reason.trim() !== '' && images.length > 0 && !submitMutation.isPending;

  const handleSubmit = () => {
    submitMutation.mutate(
      { inspectionIds, reason: reason.trim(), images },
      {
        onSuccess: () => {
          toastService.addToast({ message: RETURN_DISPOSAL_REQUEST_MESSAGE.CLAIM_SUBMIT_SUCCESS });
          onSuccess();
        },
        onError: (error) => {
          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  return (
    <Modal
      title="클레임 처리신청"
      size="xlarge"
      open={isOpen}
      confirmText="신청하기"
      disabled={!isValid}
      onCancel={onClose}
      onConfirm={handleSubmit}
    >
      {issueItemsQuery.isLoading ? (
        <div className={styles['state-area']}>
          <Spinner />
        </div>
      ) : issueItemsQuery.isError ? (
        <div className={styles['state-area']}>
          <Text size="large" color="tertiary">
            정보를 불러오지 못했습니다.
          </Text>
        </div>
      ) : (
        <ModalContentsContainer className={styles['modal-body']}>
          <div className={styles['notice-box']}>
            <span className={styles['notice-title']}>{CLAIM_NOTICE.GUIDE_TITLE}</span>
            <div className={styles['notice-lines']}>
              <span className={styles['notice-line']}>{CLAIM_NOTICE.GUIDE_DEADLINE}</span>
              <span className={styles['notice-line']}>{CLAIM_NOTICE.GUIDE_ONCE}</span>
            </div>
          </div>

          <ModalSection title="상품 정보">
            <Table<TypeReturnDisposalIssueItemApi>
              className={styles['preview-table']}
              data={issueItems}
              columns={columns}
              rowHeight={40}
              maxHeight="220px"
              loading={issueItemsQuery.isLoading}
              error={issueItemsQuery.isError}
            />
          </ModalSection>

          <ModalSection title="클레임 정보">
            <FormField
              label="클레임 사유"
              required
              contents={
                <Textarea
                  className={styles['reason-textarea']}
                  size="small"
                  height="96px"
                  count
                  maxLength={CLAIM_REASON_MAX_LENGTH}
                  placeholder={CLAIM_NOTICE.REASON_PLACEHOLDER}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              }
            />
            <FormField
              label="거래명세서 이미지"
              required
              contents={<ClaimImageUploader files={images} onChange={setImages} disabled={submitMutation.isPending} />}
            />
          </ModalSection>
        </ModalContentsContainer>
      )}
    </Modal>
  );
};

export default ReceiveReturnDisposalClaimModal;
